import React, { useEffect, useRef, useState } from 'react';
import './file.style.scss';
import {
    FileAttachment,
    FileAttachmentStatusEnum,
    FileAttachmentTypeEnum,
    FileUploadErrType,
    ReduxState,
    Response,
    TextDirection,
} from 'interface';
import { toast } from 'react-toastify';
import { connect, ConnectedProps } from 'react-redux';
import { upload } from 'scripts/api';
import { __FileProps, __FileRef, __FileType } from './file.interface';
import { Spinner } from 'react-bootstrap';
import HintText from '../hintText/hintText.index';
import QuestionImage from '../questionImage/questionImage.index';
import { UploadFileErrToast } from 'data/errorCodeToast';
import { API } from 'data';
import { FileManager } from 'scripts/fileManager';
import { Modal } from 'react-bootstrap';
import { fileValidation } from 'scripts/validation';
import { extractTitle } from 'scripts/string';
export interface SLPFileRef extends __FileRef {}
export { __FileType as SLPFileType };

const SLPFile: React.ForwardRefRenderFunction<__FileRef, ConnectedProps<typeof connector> & __FileProps> = function (
    props: ConnectedProps<typeof connector> & __FileProps,
    ref,
) {
    const lang = props.lang.utilities.SLPFile;
    const [show, setShow] = useState<string>();
    const [isUploading, setUploading] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<string>();
    const [files, setFiles] = useState<FileAttachment[]>(props.defaultValue ? props.defaultValue : []);
    const immediateValue = useRef<FileAttachment[]>(files);
    const [offlineImg, setOfflineImg] = useState<string[]>([]);
    const labelRef = useRef<HTMLLabelElement>(null);
    const formFile =
        props.fileType === __FileType.film
            ? FileAttachmentTypeEnum.video
            : props.fileType === __FileType.image
            ? FileAttachmentTypeEnum.image
            : FileAttachmentTypeEnum.file;

    React.useImperativeHandle(ref, () => ({
        getValue: () => immediateValue.current,
        setError: (text) => setError(text),
    }));

    function file2Attachment(e: File, id: string): FileAttachment {
        const titles = e.name.split('.');
        const ext = titles[1];
        return {
            file: id,
            id: id,
            created_at: new Date().toISOString(),
            workspace: props.ws ?? 0,
            extension: ext,
            name: e.name,
            filename: e.name,
            offset: 0,
            status: FileAttachmentStatusEnum.complete,
            type: FileAttachmentTypeEnum.file,
            size: e.size / 1024 / 1024,
        };
    }

    const changeHandler = (e: FileAttachment[]) => {
        immediateValue.current = e;
        setFiles(immediateValue.current);
        if (props.onChange) props.onChange(e);
        setError(undefined);
    };

    useEffect(() => {
        if (labelRef.current && props.label) labelRef.current.innerHTML = extractTitle(props.label, false);
    }, [props.label]);

    function onFileUpload(fileList: FileList | null) {
        if (fileList) {
            const allFiles = [...files];
            const extensions: string[] = [];
            const allowed = props.fileType.split(',').map((i) => (i = i.trim()));
            for (let i = 0; i < fileList.length; i++) {
                const titles = fileList[i].name.split('.');
                extensions.push('.' + titles[titles.length - 1].toLowerCase());
            }
            if (props.fileType && extensions.map((i) => allowed.includes(i)).includes(false)) {
                toast.error(lang.err400);
                return;
            }
            if (props.onDrop) props.onDrop();
            if (fileList.length > 0) setUploading(true);
            const promises: Promise<any>[] = [];
            for (let item = 0; item < fileList.length; item = item + 1) {
                const FILE = fileList[item];
                if (
                    (props.maxSize && FILE.size / 1024 / 1024 > props.maxSize) ||
                    (props.maxSize === undefined && FILE.size / 1024 / 1024 > 2)
                ) {
                    toast.error(lang.maxSizeErr);
                    continue;
                }
                const form = new FormData();
                form.append('file', FILE);
                form.append('alt', FILE.name);
                form.append('filename', FILE.name);
                form.append('name', FILE.name);
                form.append('type', formFile.toString());
                form.append('workspace', props.ws.toString());
                promises.push(
                    upload<FileAttachment>(API.upload.slp(props.accessToken ?? ''), form, false)
                        .then((res) => allFiles.push(res.data))
                        .catch((res: Response<FileUploadErrType>) => {
                            if (res.status >= 500) toast.error(props.lang.serverErr);
                            else if (res.status >= 400) UploadFileErrToast(res.data);
                            else {
                                // offline mode
                                const id = FileManager.StoreFile(FILE);
                                allFiles.push(file2Attachment(FILE, id));
                                FileManager.RenderFile(FILE).then((res) => {
                                    if (props.fileType === __FileType.image) setOfflineImg([...offlineImg, res]);
                                });
                            }
                        }),
                );
            }
            if (promises.length === 0) setUploading(false);
            Promise.all(promises).then(() => {
                setUploading(false);
                changeHandler(allFiles);
            });
        }
    }

    const onDelete = (url: string, index: number) => {
        const allFiles = files.filter((i) => i.file !== url);
        changeHandler([...allFiles]);
        if (offlineImg.length > index) {
            const temp = offlineImg;
            temp.splice(index, 1);
            setOfflineImg([...temp]);
        }
    };

    function onDropHandler(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files.length) onFileUpload(e.dataTransfer.files);
    }
    return (
        <div className={`ucheck-SLP-file-attachment  ${props.dir === TextDirection.rtl ? 'rtl-text' : ''}`}>
            <span className="d-flex">
                {props.required && <span className="required-star">*</span>}
                <span className="counter-span">{props.counter ? props.counter + '- ' : ''}</span>
                <label ref={labelRef} style={{ color: props.labelColor }} />
            </span>
            <QuestionImage src={props.image?.file} />
            <HintText value={props.hint} />
            <input
                className="d-none"
                type="file"
                accept={props.fileType}
                required={props.required}
                id="file-input-attachment"
                ref={fileRef}
                onChange={(e) => onFileUpload(e.target.files)}
                multiple={props.multiple}
            />
            <div
                className={`file-input d-flex ${isUploading ? 'uploading' : ''} ${
                    !props.multiple && files.length > 0 ? 'accept-no-more' : ''
                }`}
                onClick={() => {
                    if (fileRef.current) fileRef.current.click();
                }}
                style={{
                    border: `${props.color ? props.color + ' dashed 1px ' : ''}`,
                    backgroundColor: `${
                        props.color ? (props.color.length > 4 ? props.color + '30' : props.color + '3') : ''
                    }`,
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDrop={onDropHandler}
            >
                {isUploading ? (
                    <Spinner animation="border" size="sm" variant="dark" />
                ) : (
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <i className="material-icons-outlined">cloud_upload</i>
                            <span className="spacer" />
                            <p>
                                {lang.dnd} {props.fileType && '(' + props.fileType + ')'}
                            </p>
                        </div>
                        <span className="spacer" />
                        <div className="blue-box" style={{ backgroundColor: `${props.color ? props.color : ''}` }}>
                            {lang.uploadBtn}
                        </div>
                    </div>
                )}
            </div>
            {error && <p className="input-error">{error}</p>}
            <div className="d-flex flex-column">
                {files &&
                    files.map((item, index) => (
                        <div
                            className="uploaded-files d-flex align-items-center justify-content-between "
                            key={item.id}
                        >
                            <div className="d-flex flex-column">
                                <p>{item.name}</p>
                                <span>{item.size}</span>
                            </div>
                            <div className="d-flex">
                                {props.fileType === __FileType.image ? (
                                    <div className="d-flex">
                                        <i
                                            onClick={() =>
                                                setShow(fileValidation(item.file) ? offlineImg[index] : item.file)
                                            }
                                            className="material-icons-outlined"
                                        >
                                            visibility
                                        </i>
                                        <Modal
                                            show={show !== undefined}
                                            className="show-uploaded-image"
                                            size={'sm'}
                                            centered
                                        >
                                            <i onClick={() => setShow(undefined)} className="material-icons close-icon">
                                                close
                                            </i>
                                            <div className="ucheck-modal-body">
                                                <img src={show} />
                                            </div>
                                        </Modal>
                                    </div>
                                ) : (
                                    <a target="_blank" rel="noreferrer" href={item.file} className="d-flex">
                                        <i className="material-icons-outlined">visibility</i>
                                    </a>
                                )}
                                <i onClick={() => onDelete(item.file, index)} className="material-icons-outlined">
                                    clear
                                </i>
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    );
};

const mapStateToProps = (state: ReduxState) => ({
    lang: state.lang,
});

const connector = connect(mapStateToProps, null, null, { forwardRef: true });
export default connector(React.forwardRef(SLPFile));
